<div class="acc">

  <header class="acc__head">
    <div>
      <h1 class="acc__title">
        <i class="fa-solid fa-user-shield"></i>
        {{ 'ACCESS.TITLE' | translate }}
      </h1>
      <p class="acc__sub">{{ 'ACCESS.SUBTITLE' | translate }}</p>
    </div>
    <!-- Rien ne manque dans Keycloak, mais la synchronisation reste rejouable :
         c'est elle qui termine une reprise interrompue, ou qui la fait quand
         les rôles ont été créés à la main dans la console. Sans ce bouton,
         elle n'aurait plus de porte une fois le bandeau disparu. -->
    @if (!loading() && !errored() && !catalog().missingInKeycloak.length) {
      <button type="button" class="acc__btn acc__btn--sm acc__resync" [disabled]="syncing()"
              [attr.title]="'ACCESS.RESYNC_HINT' | translate" (click)="syncCatalog()">
        @if (syncing()) { <span class="acc__spinner acc__spinner--ink"></span> }
        <i class="fa-solid fa-rotate"></i> {{ 'ACCESS.RESYNC' | translate }}
      </button>
    }
  </header>

  <div class="acc__tabs" role="tablist">
    <button type="button" role="tab" class="acc__tab" [attr.aria-selected]="tab() === 'profiles'"
            (click)="tab.set('profiles')">{{ 'ACCESS.TAB_PROFILES' | translate }}</button>
    <button type="button" role="tab" class="acc__tab" [attr.aria-selected]="tab() === 'accounts'"
            (click)="tab.set('accounts')">{{ 'ACCESS.TAB_ACCOUNTS' | translate }}</button>
  </div>

  @if (errored()) {
    <app-error-state titleKey="ACCESS.ERROR_TITLE" messageKey="ACCESS.ERROR_DESC" [detail]="errorDetail()"
                     (retry)="load()"></app-error-state>
  } @else if (loading()) {
    <p class="acc__dim">{{ 'LOADINGS.LOADING' | translate }}</p>
  } @else {

    @if (catalog().missingInKeycloak.length) {
      <div class="acc__banner acc__banner--warn acc__banner--action" role="alert">
        <span>
          <i class="fa-solid fa-triangle-exclamation"></i>
          {{ 'ACCESS.MISSING_IN_KEYCLOAK' | translate }}
          <code>{{ catalog().missingInKeycloak.join(', ') }}</code>
        </span>
        <button type="button" class="acc__btn acc__btn--sm acc__sync" [disabled]="syncing()" (click)="syncCatalog()">
          @if (syncing()) { <span class="acc__spinner acc__spinner--ink"></span> }
          <i class="fa-solid fa-rotate"></i> {{ 'ACCESS.SYNC' | translate }}
        </button>
      </div>
    }

    <!-- Le refus de Keycloak, mot pour mot : c'est lui qui nomme le droit
         qui manque au compte de service. -->
    @if (syncError()) {
      <p class="acc__banner acc__banner--warn acc__sync-error" role="alert">
        <i class="fa-solid fa-circle-xmark"></i> {{ syncError() }}
      </p>
    }

    @if (syncResult(); as sync) {
      <div class="acc__banner acc__sync-result" role="status">
        @if (!sync.createdPermissions.length && !sync.upgradedRoles.length) {
          <i class="fa-solid fa-circle-check"></i> {{ 'ACCESS.SYNC_UP_TO_DATE' | translate }}
        } @else {
          <b><i class="fa-solid fa-circle-check"></i> {{ 'ACCESS.SYNC_DONE' | translate }}</b>
          @if (sync.createdPermissions.length) {
            <p class="acc__sync-line">
              {{ 'ACCESS.SYNC_CREATED' | translate: { count: sync.createdPermissions.length } }}
              <code>{{ sync.createdPermissions.join(', ') }}</code>
            </p>
          }
          @for (role of sync.upgradedRoles; track role.key) {
            <p class="acc__sync-line">
              {{ 'ACCESS.SYNC_UPGRADED' | translate: { label: role.label } }}
              <code>{{ role.added.join(', ') }}</code>
            </p>
          }
        }
      </div>
    }

    <!-- ═════════════ Profils ═════════════ -->
    @if (tab() === 'profiles') {
      <div class="acc__duo">
        <aside class="acc__list">
          @for (p of profiles(); track p.key) {
            <button type="button" class="acc__profile" [attr.aria-current]="draft()?.key === p.key"
                    (click)="select(p)">
              <span class="acc__profile-name">
                {{ p.label }}
                @if (p.kind !== 'profile') {
                  <span class="acc__chip" [class.acc__chip--sup]="p.kind === 'supervisor'">
                    @if (!isEditable(p)) { <i class="fa-solid fa-lock"></i> }
                    {{ ('ACCESS.KIND.' + p.kind) | translate }}
                  </span>
                }
              </span>
              <span class="acc__profile-meta">
                @if (p.kind === 'supervisor') {
                  {{ 'ACCESS.FULL_ACCESS' | translate }}
                } @else {
                  {{ 'ACCESS.PERMISSION_COUNT' | translate: { count: p.permissions.length } }}
                  @if (p.accountCount !== null && p.accountCount !== undefined) {
                    · {{ 'ACCESS.ACCOUNT_COUNT' | translate: { count: p.accountCount } }}
                  }
                }
              </span>
            </button>
          }
          <button type="button" class="acc__btn acc__btn--block" (click)="startNew()">
            <i class="fa-solid fa-plus"></i> {{ 'ACCESS.NEW_PROFILE' | translate }}
          </button>
        </aside>

        @if (draft(); as d) {
          <section class="acc__editor">
            @switch (d.key === null ? 'new' : selected()?.kind) {
              @case ('supervisor') { <p class="acc__banner">{{ 'ACCESS.BANNER_SUPERVISOR' | translate }}</p> }
              @case ('base') {
                @if (editingBase()) {
                  <p class="acc__banner acc__banner--warn">
                    {{ 'ACCESS.BANNER_BASE_EDITABLE' | translate: { count: selected()?.accountCount ?? 0 } }}
                  </p>
                } @else {
                  <p class="acc__banner">{{ 'ACCESS.BANNER_BASE' | translate }}</p>
                }
              }
              @case ('legacy') { <p class="acc__banner acc__banner--warn">{{ 'ACCESS.BANNER_LEGACY' | translate }}</p> }
              @case ('new') { <p class="acc__banner">{{ 'ACCESS.BANNER_NEW' | translate }}</p> }
              @default {
                @if ((selected()?.accountCount ?? 0) > 0) {
                  <p class="acc__banner acc__banner--warn">
                    {{ 'ACCESS.BANNER_IN_USE' | translate: { count: selected()?.accountCount } }}
                  </p>
                }
              }
            }

            <div class="acc__fields">
              <label class="acc__field">
                {{ 'ACCESS.NAME' | translate }}
                <input type="text" maxlength="60" [ngModel]="d.label" [disabled]="readOnly() || editingBase()"
                       (ngModelChange)="patchDraft({ label: $event })" />
              </label>
              <label class="acc__field">
                {{ 'ACCESS.DESCRIPTION' | translate }}
                <input type="text" maxlength="200" [ngModel]="d.description" [disabled]="readOnly() || editingBase()"
                       (ngModelChange)="patchDraft({ description: $event })" />
              </label>
            </div>

            <p class="acc__dim acc__legend">
              <span class="acc__chip">{{ 'ACCESS.ENFORCED' | translate }}</span>
              <span class="acc__chip acc__chip--warn">{{ 'ACCESS.NOT_ENFORCED' | translate }}</span>
              {{ 'ACCESS.ENFORCED_LEGEND' | translate }}
            </p>

            @if (!readOnly()) {
              <div class="acc__presets">
                <button type="button" class="acc__btn acc__btn--sm" (click)="allRead()">{{ 'ACCESS.ALL_READ' | translate }}</button>
                <button type="button" class="acc__btn acc__btn--sm" (click)="clearAll()">{{ 'ACCESS.CLEAR_ALL' | translate }}</button>
              </div>
            }

            <app-permission-picker
              [modules]="catalog().modules"
              [selected]="d.permissions"
              [readOnly]="readOnly()"
              [locked]="isOwnClaimLocked"
              [lockedTitle]="'ACCESS.OWN_CLAIM_ROLE' | translate"
              [extras]="permissionChips"
              (toggled)="toggle($event)"
              (moduleToggled)="toggleModule($event)"></app-permission-picker>

            <!-- Les pastilles propres à cet écran, sous chaque permission. -->
            <ng-template #permissionChips let-perm>
              @if (perm.enforced) {
                <span class="acc__chip">{{ 'ACCESS.ENFORCED' | translate }}</span>
              } @else {
                <span class="acc__chip acc__chip--warn" [title]="'ACCESS.NOT_ENFORCED_HINT' | translate">
                  {{ 'ACCESS.NOT_ENFORCED' | translate }}
                </span>
              }
              @if (editingBase() && perm.ownClaim) {
                <span class="acc__chip acc__chip--own">
                  <i class="fa-solid fa-id-badge"></i> {{ 'ACCESS.OWN_CLAIM' | translate }}
                </span>
              }
            </ng-template>

            @if (!readOnly()) {
              <footer class="acc__foot">
                @if (d.key && !editingBase()) {
                  <button type="button" class="acc__btn acc__btn--danger" (click)="remove()">
                    <i class="fa-solid fa-trash"></i> {{ 'COMMON.DELETE' | translate }}
                  </button>
                }
                <span class="acc__push"></span>
                <span class="acc__dim">{{ 'ACCESS.TOKEN_DELAY' | translate }}</span>
                <button type="button" class="acc__btn acc__btn--primary" [disabled]="!canSave()" (click)="save()">
                  @if (saving()) { <span class="acc__spinner"></span> }
                  <i class="fa-solid fa-floppy-disk"></i> {{ 'COMMON.SAVE' | translate }}
                </button>
              </footer>
            }
          </section>
        }
      </div>
    }

    <!-- ═════════════ Comptes ═════════════ -->
    @if (tab() === 'accounts') {
      <div class="acc__tools">
        <input type="search" class="acc__search" [ngModel]="search()" (ngModelChange)="search.set($event)"
               [placeholder]="'ACCESS.SEARCH' | translate" [attr.aria-label]="'ACCESS.SEARCH' | translate" />
        @for (f of ['all', 'admin', 'professor']; track f) {
          <button type="button" class="acc__btn acc__btn--sm" [attr.aria-pressed]="filter() === f"
                  (click)="filter.set($any(f))">{{ ('ACCESS.FILTER.' + f) | translate }}</button>
        }
      </div>

      <table class="acc__table">
        <thead>
          <tr>
            <th>{{ 'ACCESS.COL_ACCOUNT' | translate }}</th>
            <th>{{ 'ACCESS.COL_BASE' | translate }}</th>
            <th>{{ 'ACCESS.COL_PROFILES' | translate }}</th>
            <th>{{ 'ACCESS.COL_PERMISSIONS' | translate }}</th>
            <th><span class="acc__sr">{{ 'ACCESS.COL_ACTIONS' | translate }}</span></th>
          </tr>
        </thead>
        <tbody>
          @for (a of visibleAccounts(); track a.userId) {
            <tr>
              <td>
                <b>{{ a.fullName }}</b>
                <span class="acc__mail">{{ a.email }}</span>
              </td>
              <td>
                @for (r of a.baseRoles; track r) {
                  <span class="acc__chip" [class.acc__chip--sup]="r === 'supervisor'">{{ ('ACCESS.ROLE.' + r) | translate }}</span>
                }
              </td>
              <td>
                @if (isSupervisor(a)) {
                  <span class="acc__dim">{{ 'ACCESS.FULL_ACCESS' | translate }}</span>
                } @else if (a.profiles.length) {
                  @for (k of a.profiles; track k) { <span class="acc__chip">{{ profileLabel(k) }}</span> }
                } @else {
                  <span class="acc__dim">{{ 'ACCESS.NO_PROFILE' | translate }}</span>
                }
              </td>
              <td>
                @if (isSupervisor(a)) {
                  <span class="acc__dim">{{ 'ACCESS.FULL_ACCESS' | translate }}</span>
                } @else {
                  <span class="acc__effective">
                    {{ 'ACCESS.PERMISSION_COUNT' | translate: { count: a.effectivePermissions?.length ?? 0 } }}
                  </span>
                  @if (a.directPermissions?.length) {
                    <span class="acc__chip acc__chip--direct" [attr.title]="'ACCESS.DIRECT_HINT' | translate">
                      <i class="fa-solid fa-user-check"></i>
                      {{ 'ACCESS.DIRECT_COUNT' | translate: { count: a.directPermissions?.length } }}
                    </span>
                  }
                }
              </td>
              <td class="acc__end">
                @if (!isSupervisor(a)) {
                  <button type="button" class="acc__btn acc__btn--sm" (click)="openAssign(a)">
                    {{ 'ACCESS.MANAGE' | translate }}
                  </button>
                }
              </td>
            </tr>
          } @empty {
            <tr><td colspan="5" class="acc__dim">{{ 'ACCESS.NO_ACCOUNT' | translate }}</td></tr>
          }
        </tbody>
      </table>
    }
  }

  <dialog #assignDialog class="acc__dialog" (close)="onAssignClosed()">
    @if (editing(); as a) {
      <h2 class="acc__dialog-title">{{ 'ACCESS.ASSIGN_TITLE' | translate: { name: a.fullName } }}</h2>
      <div class="acc__dialog-body">
        <h3 class="acc__scope-title">{{ 'ACCESS.SECTION_PROFILES' | translate }}</h3>
        @for (p of assignable(); track p.key) {
          <label class="acc__option">
            <input type="checkbox" [checked]="choice().has(p.key)" (change)="toggleChoice(p.key)" />
            <span>
              <b>{{ p.label }}</b>
              <span class="acc__chip">{{ 'ACCESS.PERMISSION_COUNT' | translate: { count: p.permissions.length } }}</span>
              @if (p.description) { <span class="acc__dim acc__block">{{ p.description }}</span> }
            </span>
          </label>
        } @empty {
          <p class="acc__dim">{{ 'ACCESS.NO_ASSIGNABLE' | translate }}</p>
        }

        @if (scopeVisible()) {
          <section class="acc__scope">
            <h3 class="acc__scope-title">{{ 'SCOPE.TITLE' | translate }}</h3>
            <p class="acc__dim">{{ 'SCOPE.SUBTITLE' | translate }}</p>
            @if (scopeLoaded()) {
              <app-inspection-scope-editor [subjects]="referentialSubjects()" [value]="scope()"
                                           (valueChange)="onScopeChange($event)"></app-inspection-scope-editor>
            } @else {
              <p class="acc__dim">{{ 'SCOPE.UNAVAILABLE' | translate }}</p>
            }
          </section>
        }

        <!-- L'accès qu'on ne donne qu'à cette personne. Ce que le compte tient
             déjà d'ailleurs est coché et figé, avec sa source : on ne le
             redonne pas, et on ne le retire qu'à la source. -->
        <section class="acc__scope acc__direct">
          <h3 class="acc__scope-title">{{ 'ACCESS.SECTION_DIRECT' | translate }}</h3>
          <p class="acc__dim">{{ 'ACCESS.DIRECT_INTRO' | translate }}</p>

          @for (m of catalog().modules; track m.id) {
            <details class="acc__module acc__fold" [open]="directCount(m) > 0">
              <summary class="acc__module-head acc__fold-head">
                <i class="fa-solid fa-chevron-down acc__fold-mark" aria-hidden="true"></i>
                {{ label(m) }}
                @if (directCount(m) > 0) {
                  <span class="acc__chip acc__chip--direct">
                    {{ 'ACCESS.DIRECT_COUNT' | translate: { count: directCount(m) } }}
                  </span>
                }
                <span class="acc__module-count">{{ heldCount(m) }} / {{ m.permissions.length }}</span>
              </summary>
              <div class="acc__perms">
                @for (perm of m.permissions; track perm.key) {
                  <label class="acc__perm" [class.acc__perm--off]="directLocked(perm)"
                         [attr.title]="!hasPermission(perm.key) && ownClaimUseless(perm)
                                         ? ('ACCESS.OWN_CLAIM_ACCOUNT' | translate) : null">
                    <input type="checkbox" [checked]="hasPermission(perm.key)" [disabled]="directLocked(perm)"
                           (change)="toggleDirect(perm)" />
                    <span>
                      {{ label(perm) }}
                      <span class="acc__perm-key">
                        <code>{{ perm.key }}</code>
                        @for (origin of originsOf(perm.key); track $index) {
                          <span class="acc__chip acc__chip--origin">
                            {{ origin.translate ? (origin.label | translate) : origin.label }}
                          </span>
                        }
                        @if (direct().has(perm.key)) {
                          <span class="acc__chip acc__chip--direct">{{ 'ACCESS.ORIGIN.direct' | translate }}</span>
                        }
                        @if (!hasPermission(perm.key) && ownClaimUseless(perm)) {
                          <span class="acc__chip acc__chip--own">
                            <i class="fa-solid fa-id-badge"></i> {{ 'ACCESS.OWN_CLAIM' | translate }}
                          </span>
                        }
                      </span>
                    </span>
                  </label>
                }
              </div>
            </details>
          }
        </section>

        <div class="acc__gains">
          <b>{{ 'ACCESS.GAINS' | translate }}</b>
          <div class="acc__chips">
            @for (k of gains(); track k) {
              <span class="acc__chip" [class.acc__chip--warn]="!isEnforced(k)">{{ permissionLabel(k) }}</span>
            } @empty {
              <span class="acc__dim">{{ 'ACCESS.NO_GAIN' | translate }}</span>
            }
          </div>
          <p class="acc__dim">{{ 'ACCESS.TOKEN_DELAY' | translate }}</p>
        </div>
      </div>
      <footer class="acc__dialog-foot">
        <button type="button" class="acc__btn" (click)="closeAssign()">{{ 'COMMON.CANCEL' | translate }}</button>
        <button type="button" class="acc__btn acc__btn--primary" [disabled]="saving()" (click)="confirmAssign()">
          {{ 'COMMON.SAVE' | translate }}
        </button>
      </footer>
    }
  </dialog>
</div>
